import { useCallback, useMemo } from "react"

import { CatalogItemDetailsForm } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { EQUIPMENT_CONDITIONS, EQUIPMENT_CONDITIONS_VALUES } from "../../constants/equipmentConditions"
import { useEquipmentUpdate } from "../../hooks/mutations/useEquipmentUpdate"
import { useEquipmentProject } from "../../hooks/queries/equipment/useEquipmentProject"
import { useEquipment } from "../../hooks/queries/equipment/useEquipments"
import { useEquipmentTypes } from "../../hooks/queries/equipment/useEquipmentsType"

import * as Styled from "./EquipmentDetailsForm.styled"

export const EquipmentDetailsFormPost = (props) => {

    const {
        className,
        close,
        signId,
        ...otherProps
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const navigate = useNavigateWithSearchParams()

    const { data: equipment, isLoading, isRefetching, isError } = useEquipment(signId)
    const { data: project } = useEquipmentProject()
    const { data: equipmentTypes, isLoading: isLoadingTypes, isError: isErrorTypes } = useEquipmentTypes()

    const existingTags = useMemo(() => {
        if (project?.tags) {
            return project.tags.sort()
        }

        return []
    }, [project])

    const conditions = useMemo(() => {
        return EQUIPMENT_CONDITIONS.filter(condition => condition > 0)
            .map(condition => ({
                value: condition,
                label: t(I18N_NAMESPACE, EQUIPMENT_CONDITIONS_VALUES[condition].label),
            }))
    }, [t])

    const purchasedEquipmentTypes = useMemo(() => {
        const filterPurchasedEquipment = (equipmentTypes) => {
            return equipmentTypes
                .filter(equipmentType => equipmentType.is_purchased)
                .map(equipmentType => ({
                    ...equipmentType,
                    children: filterPurchasedEquipment(equipmentType.children),
                }))
        }

        return filterPurchasedEquipment(equipmentTypes)
    }, [equipmentTypes])

    const mutation = useEquipmentUpdate(signId, {}, {
        onSuccess: (data) => navigate(`../${data.linear_location.road}/${data.uuid}`),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.equipmentDetailsForm.snackbar.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.equipmentDetailsForm.snackbar.success"),
    })

    const onSubmit = useCallback((values) => {
        mutation.mutate(values)
        close(false)
    }, [close, mutation])

    if (isLoading || isRefetching || isLoadingTypes) {
        return <Styled.FormSkeleton className={className} />
    }

    if (isError || isErrorTypes) {
        return <Styled.ErrorMessage>{t(I18N_NAMESPACE, "containers.equipmentDetailsForm.error")}</Styled.ErrorMessage>
    }

    return <CatalogItemDetailsForm
        className={className}
        {...otherProps}
        forceTextContent
        initialValues={equipment}
        catalogTypes={purchasedEquipmentTypes}
        typeField="equipment_type"
        typeFieldLabel={t(I18N_NAMESPACE, "containers.equipmentDetailsForm.type")}
        conditions={conditions}
        existingTags={existingTags}
        onSubmit={onSubmit}
        onCancel={close}
    />
}

EquipmentDetailsFormPost.propTypes = {
    className: PropTypes.string,
    close: PropTypes.func.isRequired,
    signId: PropTypes.string.isRequired,
}